import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Card } from '@parrot-software/design-system';
import { ChevronRight } from '@parrot-software/design-system/icons';
import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import { displayValue, formatBrand } from '@subscriptions/presentation/utils';

import type { WizardOrigin } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import { CREATE_ORIGIN } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';

import { cardConfirmationStyles as styles } from './CardConfirmation.styles';
import { ConfirmationSuccessHeader } from './ConfirmationSuccessHeader';
import { ConfirmationSummaryRows } from './ConfirmationSummaryRows';

type CardUpdatedConfirmationProps = {
  blockUuid: string;
  origin: WizardOrigin;
  paymentMethod: PaymentMethodViewDto;
};

/**
 * Terminal card confirmation (wizard step 5, `card-updated` variant — Figma
 * 3002-119192, saved card attached via attach-payment-method).
 *
 * Shown after a saved org card is attached to the block from the saved-cards
 * step. The title switches on the wizard entry origin (product override of the
 * Figma's "¡Tarjeta actualizada!"): "¡Sucursales agrupadas y tarjeta guardada!"
 * in the creation flow, "¡Método de pago actualizado!" when re-entering from the
 * block detail to change/configure the payment method.
 * Renders the attached method (brand + `•••• last4` via `formatBrand`), its
 * expiration and the monthly auto-charge note. The card fields come from the
 * `attach-payment-method` response carried in route state, already normalized
 * into the shared {@link PaymentMethodViewDto}. The primary action ("Ir a mi
 * suscripción", product override of the Figma's "Listo") routes to the block
 * detail page.
 *
 * @param props.blockUuid - Subscription block uuid for the in-flight wizard
 * @param props.origin - Flow the wizard was entered through
 * @param props.paymentMethod - Card payment method returned by attach-payment-method
 * @returns React element rendering the card-updated confirmation
 */
export const CardUpdatedConfirmation = ({ blockUuid, origin, paymentMethod }: CardUpdatedConfirmationProps) => {
  const { t } = useTranslation('subscriptions');
  const history = useHistory();

  const handleGoToSubscription = () => {
    history.push(`/subscriptions/blocks/${blockUuid}`);
  };

  return (
    <div className={styles.root}>
      <ConfirmationSuccessHeader
        iconSize={60}
        layout="stacked"
        title={
          origin === CREATE_ORIGIN
            ? t('blockCreationWizard.confirmation.cardSaved.groupedTitle')
            : t('blockCreationWizard.confirmation.cardUpdated.titleUpdated')
        }
      />

      <Card className={styles.card}>
        <ConfirmationSummaryRows
          rows={[
            {
              label: t('paymentMethod.card'),
              value: t('paymentMethod.cardLabel', {
                brand: formatBrand(paymentMethod.brand),
                lastFourDigits: displayValue(paymentMethod.lastFourDigits),
              }),
            },
            {
              label: t('blockCreationWizard.confirmation.card.expirationLabel'),
              value: displayValue(paymentMethod.expirationDate),
            },
          ]}
        />
      </Card>

      <div className={styles.noteBox}>
        <p className={styles.notePrimary}>{t('blockCreationWizard.confirmation.cardSaved.autoChargeNote')}</p>
        <p className={styles.noteSecondary}>{t('blockCreationWizard.confirmation.cardSaved.confirmationEmailNote')}</p>
      </div>

      <Button className={styles.primaryButton} onClick={handleGoToSubscription}>
        {t('blockCreationWizard.confirmation.primaryAction')}
        <ChevronRight aria-hidden className="h-4 w-4" />
      </Button>
    </div>
  );
};
